import * as React from 'react';
import {
    Alert,
    Box,
    Chip,
    CircularProgress,
    Button,
    Dialog,
    DialogContent,
    DialogTitle,
    FormControl,
    InputLabel,
    Link,
    MenuItem,
    Pagination,
    Select,
    Stack,
    TextField,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid/DataGrid/DataGrid';
import { GridColDef } from '@mui/x-data-grid/models/colDef/gridColDef';
import { GridCellParams } from '@mui/x-data-grid/models/params/gridCellParams';
import { useNavigate } from 'react-router-dom';
import { getQuizParticipationAdmin, getQuizParticipations } from '../Api';
import {
    QuizParticipationAdminDetailDto,
    QuizParticipationAdminItemDto,
    QuizParticipationAnswerDto,
    QUIZ_PARTICIPATION_STATE_OPTIONS,
    QUIZ_STATE_OPTIONS,
    matchStateLabel,
    quizParticipationStateLabel,
    quizStateLabel,
} from '../models/dtos/QuizAdminDtos';

function formatDate(value?: string | null) {
    return value ? new Date(value).toLocaleString() : '—';
}

function formatMoney(value?: number | null) {
    if (value == null || Number.isNaN(value)) return '—';
    return Math.abs(value - Math.round(value)) < 0.001
        ? Math.round(value).toLocaleString()
        : value.toLocaleString(undefined, { maximumFractionDigits: 2 });
}

function formatCoef(value?: number | null) {
    if (value == null || Number.isNaN(value)) return '—';
    return value.toLocaleString(undefined, { maximumFractionDigits: 4 });
}

function formatScore(home?: number | null, away?: number | null) {
    if (home == null && away == null) return '';
    const left = home == null ? '?' : String(home);
    const right = away == null ? '?' : String(away);
    return `${left} - ${right}`;
}

function stateColor(state: number | null | undefined): 'default' | 'success' | 'error' | 'warning' | 'info' {
    switch (state) {
        case 4: return 'success';
        case 8: return 'error';
        case 2: return 'warning';
        case 1: return 'info';
        default: return 'default';
    }
}

function CheckCell({ row }: { row: QuizParticipationAdminItemDto }) {
    const calc = row.calculation;
    if (!calc.settled) {
        return (
            <Chip
                size="small"
                color={calc.reviewNeeded ? 'warning' : 'default'}
                label={calc.reviewNeeded ? 'Waiting on answers' : 'Not settled'}
            />
        );
    }
    if (!calc.savedByCron) {
        return <Chip size="small" color="warning" label="Not calculated" />;
    }
    if (!calc.matchesStored) {
        return <Chip size="small" color="error" label="Mismatch" />;
    }
    if (calc.reviewNeeded) {
        return <Chip size="small" color="warning" label="Review" />;
    }
    return <Chip size="small" color="success" label="OK" />;
}

function formatLine(value?: number | null) {
    if (value == null || Number.isNaN(value)) return '—';
    return Math.abs(value - Math.round(value)) < 0.001
        ? String(Math.round(value))
        : value.toLocaleString(undefined, { maximumFractionDigits: 2 });
}

function currentOddsLabel(answer: QuizParticipationAnswerDto) {
    if (answer.currentOdds == null) return 'No live price';
    const price = formatCoef(answer.currentOdds);
    if (answer.currentOddsAvailable == null) return price;
    return `${price} · ${answer.currentOddsAvailable ? 'market open' : 'market closed'}`;
}

export default function UserQuizzesTable({ userId, showTitle = false }: { userId?: number; showTitle?: boolean }) {
    const navigate = useNavigate();
    const limit = 25;
    const [page, setPage] = React.useState(0);
    const [stateFilter, setStateFilter] = React.useState<number | null>(null);
    const [quizStateFilter, setQuizStateFilter] = React.useState<number | null>(null);
    const [userIdDraft, setUserIdDraft] = React.useState('');
    const [searchDraft, setSearchDraft] = React.useState('');
    const [appliedUserId, setAppliedUserId] = React.useState<number | null>(null);
    const [appliedSearch, setAppliedSearch] = React.useState('');
    const [rows, setRows] = React.useState<QuizParticipationAdminItemDto[]>([]);
    const [totalItems, setTotalItems] = React.useState(0);
    const [loaded, setLoaded] = React.useState(false);
    const [error, setError] = React.useState<string | null>(null);
    const [detail, setDetail] = React.useState<QuizParticipationAdminDetailDto | null>(null);
    const [detailLoading, setDetailLoading] = React.useState(false);
    const [detailError, setDetailError] = React.useState<string | null>(null);
    const [openId, setOpenId] = React.useState<number | null>(null);

    const load = React.useCallback(async (nextPage: number) => {
        setLoaded(false);
        setError(null);
        try {
            const response = await getQuizParticipations(nextPage * limit, limit, {
                userId: userId ?? appliedUserId,
                state: stateFilter,
                quizState: quizStateFilter,
                searchTerm: appliedSearch || null,
            });
            setRows(response.items ?? []);
            setTotalItems(response.totalItems ?? 0);
            setPage(nextPage);
            setLoaded(true);
        } catch (e) {
            setRows([]);
            setTotalItems(0);
            setError(e instanceof Error ? e.message : 'Failed to load quizzes');
            setLoaded(true);
        }
    }, [appliedSearch, appliedUserId, quizStateFilter, stateFilter, userId]);

    React.useEffect(() => {
        load(0);
    }, [load]);

    const applyTextFilters = () => {
        const rawUserId = userIdDraft.trim();
        if (rawUserId !== '' && !/^\d+$/.test(rawUserId)) {
            setError('User ID must be a positive number');
            return;
        }
        setAppliedUserId(rawUserId === '' ? null : Number(rawUserId));
        setAppliedSearch(searchDraft.trim());
    };

    const openDetail = async (id: number) => {
        setOpenId(id);
        setDetail(null);
        setDetailError(null);
        setDetailLoading(true);
        try {
            const data = await getQuizParticipationAdmin(id);
            setDetail(data);
        } catch (e) {
            setDetailError(e instanceof Error ? e.message : 'Failed to load quiz');
        } finally {
            setDetailLoading(false);
        }
    };

    const columns: GridColDef[] = React.useMemo(() => [
        {
            field: 'createdAt',
            headerName: 'Played',
            width: 170,
            renderCell: (params: GridCellParams) => <>{formatDate(params.row.createdAt)}</>,
        },
        ...(userId == null ? [{
            field: 'userName',
            headerName: 'User',
            width: 180,
            renderCell: (params: GridCellParams) => {
                const row = params.row as QuizParticipationAdminItemDto;
                const label = `${row.userName ?? 'User'} #${row.userId}`;
                return (
                    <Link
                        component="button"
                        type="button"
                        underline="hover"
                        onClick={e => {
                            e.stopPropagation();
                            navigate('/users/' + row.userId + '?tab=quizzes');
                        }}
                    >
                        {label}
                    </Link>
                );
            },
        } as GridColDef] : []),
        {
            field: 'match',
            headerName: 'Match',
            width: 280,
            renderCell: (params: GridCellParams) => {
                const row = params.row as QuizParticipationAdminItemDto;
                const teams = `${row.homeTeamName ?? '?'} vs ${row.awayTeamName ?? '?'}`;
                const score = formatScore(row.scoreHome, row.scoreAway);
                const label = `${row.matchId != null ? `#${row.matchId} ` : ''}${teams}${score ? ` (${score})` : ''}`;
                if (row.matchId == null) return <>{label}</>;
                return (
                    <Link
                        component="button"
                        type="button"
                        underline="hover"
                        onClick={e => {
                            e.stopPropagation();
                            navigate('/matches/' + row.matchId);
                        }}
                    >
                        {label}
                    </Link>
                );
            },
        },
        {
            field: 'quizState',
            headerName: 'Quiz',
            width: 130,
            renderCell: (params: GridCellParams) => {
                const row = params.row as QuizParticipationAdminItemDto;
                return <>#{row.quizId} {quizStateLabel(row.quizState)}</>;
            },
        },
        {
            field: 'state',
            headerName: 'Result',
            width: 120,
            renderCell: (params: GridCellParams) => (
                <Chip size="small" color={stateColor(params.row.state)} label={quizParticipationStateLabel(params.row.state)} />
            ),
        },
        {
            field: 'answers',
            headerName: 'Answers',
            width: 150,
            renderCell: (params: GridCellParams) => {
                const row = params.row as QuizParticipationAdminItemDto;
                return <>{row.wonAnswers} won · {row.lostAnswers} lost · {row.pendingAnswers} pending</>;
            },
        },
        {
            field: 'tierName',
            headerName: 'Tier',
            width: 130,
            renderCell: (params: GridCellParams) => {
                const row = params.row as QuizParticipationAdminItemDto;
                return <>{row.tierName ?? '—'} ×{formatCoef(row.oddsBoost)}</>;
            },
        },
        {
            field: 'moneyPlaced',
            headerName: 'Placed',
            width: 100,
            renderCell: (params: GridCellParams) => <>{formatMoney(params.row.moneyPlaced)}</>,
        },
        {
            field: 'moneyReturned',
            headerName: 'Returned',
            width: 110,
            renderCell: (params: GridCellParams) => <>{formatMoney(params.row.moneyReturned)}</>,
        },
        {
            field: 'totalCoefficient',
            headerName: 'Total odds',
            width: 110,
            renderCell: (params: GridCellParams) => <>{formatCoef(params.row.totalCoefficient)}</>,
        },
        {
            field: 'check',
            headerName: 'Check',
            width: 120,
            sortable: false,
            renderCell: (params: GridCellParams) => <CheckCell row={params.row as QuizParticipationAdminItemDto} />,
        },
    ], [navigate, userId]);

    const totalPages = Math.max(1, Math.ceil(totalItems / limit));
    const participation = detail?.participation;
    const calc = participation?.calculation;

    return (
        <Stack gap={2} sx={{ width: '100%' }}>
            {showTitle ? <Typography variant="h4">Quizzes</Typography> : null}
            <Typography variant="body2" color="text.secondary">
                {userId != null
                    ? 'Quizzes this user played. Open a row to see the bet, the odds used for the payout, and the current market odds.'
                    : 'All quiz participations. Filter by user, match, or result, then open a row to check the bet and the odds.'}
                {' '}A lost answer makes the quiz Lost. Otherwise the payout is stake × tier boost × odds of the won answers.
            </Typography>
            <Stack direction="row" gap={2} alignItems="center" flexWrap="wrap">
                <FormControl size="small" sx={{ minWidth: 160 }}>
                    <InputLabel>Result</InputLabel>
                    <Select
                        label="Result"
                        value={stateFilter ?? ''}
                        onChange={e => setStateFilter(e.target.value === '' ? null : Number(e.target.value))}
                    >
                        <MenuItem value="">All</MenuItem>
                        {QUIZ_PARTICIPATION_STATE_OPTIONS.map(option => (
                            <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 160 }}>
                    <InputLabel>Quiz</InputLabel>
                    <Select
                        label="Quiz"
                        value={quizStateFilter ?? ''}
                        onChange={e => setQuizStateFilter(e.target.value === '' ? null : Number(e.target.value))}
                    >
                        <MenuItem value="">All</MenuItem>
                        {QUIZ_STATE_OPTIONS.map(option => (
                            <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                        ))}
                    </Select>
                </FormControl>
                {userId == null ? (
                    <TextField
                        size="small"
                        label="User ID"
                        value={userIdDraft}
                        onChange={e => setUserIdDraft(e.target.value)}
                        onKeyDown={e => {
                            if (e.key === 'Enter') applyTextFilters();
                        }}
                        sx={{ width: 140 }}
                    />
                ) : null}
                <TextField
                    size="small"
                    label="Search user, team, quiz or match"
                    value={searchDraft}
                    onChange={e => setSearchDraft(e.target.value)}
                    onKeyDown={e => {
                        if (e.key === 'Enter') applyTextFilters();
                    }}
                    sx={{ minWidth: 260 }}
                />
                <Button variant="outlined" onClick={applyTextFilters}>Search</Button>
                {loaded && !error ? (
                    <Typography variant="body2" color="text.secondary">
                        {totalItems} quiz{totalItems === 1 ? '' : 'zes'}
                    </Typography>
                ) : null}
            </Stack>

            {error ? <Alert severity="error">{error}</Alert> : null}
            {!loaded ? <CircularProgress size={48} /> : null}
            {loaded && !error && rows.length === 0 ? (
                <Alert severity="info">{userId != null ? 'No quizzes for this user.' : 'No quizzes found.'}</Alert>
            ) : null}
            {loaded && rows.length > 0 ? (
                <>
                    <Pagination
                        count={totalPages}
                        page={page + 1}
                        onChange={(_, next) => load(next - 1)}
                    />
                    <DataGrid
                        sx={{ width: '100%' }}
                        rows={rows}
                        columns={columns}
                        getRowId={row => row.id}
                        onRowClick={params => openDetail(params.row.id)}
                        disableRowSelectionOnClick
                        hideFooter
                        autoHeight
                    />
                </>
            ) : null}

            <Dialog open={openId != null} onClose={() => setOpenId(null)} maxWidth="lg" fullWidth>
                <DialogTitle>
                    {participation
                        ? `Quiz #${participation.quizId} · participation #${participation.id}`
                        : 'Quiz participation'}
                </DialogTitle>
                <DialogContent>
                    {detailLoading ? <CircularProgress size={32} /> : null}
                    {detailError ? <Alert severity="error">{detailError}</Alert> : null}
                    {participation && calc ? (
                        <Stack gap={2} sx={{ pt: 1 }}>
                            <Stack direction="row" gap={1} flexWrap="wrap">
                                <Chip label={`Quiz ${quizStateLabel(participation.quizState)}`} />
                                <Chip color={stateColor(participation.state)} label={quizParticipationStateLabel(participation.state)} />
                                <Chip label={`Match ${matchStateLabel(participation.matchState)}`} />
                                {!calc.settled ? <Chip color={calc.reviewNeeded ? 'warning' : 'default'} label={calc.reviewNeeded ? 'Waiting on answers' : 'Not settled'} /> : null}
                                {calc.settled && !calc.savedByCron ? <Chip color="warning" label="Not calculated" /> : null}
                                {calc.settled && calc.savedByCron && !calc.matchesStored ? <Chip color="error" label="Mismatch" /> : null}
                                {calc.settled && calc.savedByCron && calc.matchesStored && calc.reviewNeeded ? <Chip color="warning" label="Review" /> : null}
                                {calc.settled && calc.savedByCron && calc.matchesStored && !calc.reviewNeeded ? <Chip color="success" label="Matches formula" /> : null}
                            </Stack>
                            <Typography>
                                {(participation.homeTeamName ?? '?') + ' vs ' + (participation.awayTeamName ?? '?')}
                                {formatScore(participation.scoreHome, participation.scoreAway)
                                    ? ` · ${formatScore(participation.scoreHome, participation.scoreAway)}`
                                    : ''}
                                {participation.matchId != null ? (
                                    <>
                                        {' · '}
                                        <Link component="button" type="button" onClick={() => navigate('/matches/' + participation.matchId)}>
                                            Open match #{participation.matchId}
                                        </Link>
                                    </>
                                ) : null}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                <Link
                                    component="button"
                                    type="button"
                                    onClick={() => navigate('/users/' + participation.userId + '?tab=quizzes')}
                                >
                                    {participation.userName ?? 'User'} #{participation.userId}
                                </Link>
                                {' · '}played {formatDate(participation.createdAt)}
                                {participation.matchStartDate ? ` · kickoff ${formatDate(participation.matchStartDate)}` : ''}
                                {' · '}
                                {participation.tierName ?? 'Tier'} (fee {formatMoney(participation.tierFee)}, boost ×{formatCoef(participation.oddsBoost)})
                            </Typography>
                            <Box>
                                <Typography variant="subtitle2">Saved</Typography>
                                <Typography variant="body2">
                                    State {quizParticipationStateLabel(participation.state)}
                                    {' · '}stake {formatMoney(participation.moneyPlaced)}
                                    {' · '}payout {formatMoney(participation.moneyReturned)}
                                    {' · '}total odds {formatCoef(participation.totalCoefficient)}
                                </Typography>
                            </Box>
                            <Box>
                                <Typography variant="subtitle2">Expected from answers</Typography>
                                <Typography variant="body2">{calc.summary}</Typography>
                                {calc.expectedState != null ? (
                                    <Typography variant="body2">
                                        Expected state {quizParticipationStateLabel(calc.expectedState)}
                                        {calc.expectedMoneyReturned != null ? ` · payout ${formatMoney(calc.expectedMoneyReturned)}` : ''}
                                        {calc.expectedCoefficient != null ? ` · coefficient ${formatCoef(calc.expectedCoefficient)}` : ''}
                                    </Typography>
                                ) : null}
                            </Box>
                            {calc.warnings.length > 0 ? (
                                <Alert severity={calc.savedByCron && !calc.matchesStored ? 'error' : 'warning'}>
                                    {calc.warnings.map(warning => (
                                        <div key={warning}>{warning}</div>
                                    ))}
                                </Alert>
                            ) : null}
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Q</TableCell>
                                        <TableCell>Pick</TableCell>
                                        <TableCell>Bet type</TableCell>
                                        <TableCell>Market</TableCell>
                                        <TableCell>Line</TableCell>
                                        <TableCell>Odds used</TableCell>
                                        <TableCell>Odds now</TableCell>
                                        <TableCell>Line score</TableCell>
                                        <TableCell>Answer result</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {(detail?.answers ?? []).map(answer => {
                                        const placedDiffers = answer.placedOdds != null
                                            && Math.abs(answer.placedOdds - answer.coefficient) >= 0.0001;
                                        return (
                                            <TableRow key={answer.placedQuizBetId}>
                                                <TableCell>{answer.questionNumber}</TableCell>
                                                <TableCell>{answer.betName || `Bet #${answer.quizBetId}`}</TableCell>
                                                <TableCell>{answer.marketBetTypeName || answer.betTypeName || '—'}</TableCell>
                                                <TableCell>{answer.groupName ?? '—'}</TableCell>
                                                <TableCell>{formatLine(answer.betTypeParam)}</TableCell>
                                                <TableCell>
                                                    {formatCoef(answer.coefficient)}
                                                    {placedDiffers ? ` (locked ${formatCoef(answer.placedOdds)})` : ''}
                                                </TableCell>
                                                <TableCell>{currentOddsLabel(answer)}</TableCell>
                                                <TableCell>
                                                    {formatScore(answer.varietyScoreHome, answer.varietyScoreAway) || '—'}
                                                    {answer.varietyCancelled ? ' cancelled' : ''}
                                                    {answer.varietyFinished ? '' : answer.varietyScoreHome == null ? '' : ' live'}
                                                </TableCell>
                                                <TableCell>
                                                    <Chip
                                                        size="small"
                                                        color={stateColor(answer.placedBetState)}
                                                        label={quizParticipationStateLabel(answer.placedBetState)}
                                                    />
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                            <Typography variant="caption" color="text.secondary">
                                Odds used is the price locked on the quiz question. The payout multiplies that price, not the live one. Odds now is the latest grabbed price for the same bet type and line.
                            </Typography>
                        </Stack>
                    ) : null}
                </DialogContent>
            </Dialog>
        </Stack>
    );
}
